import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  GraduationCap, 
  Briefcase, 
  Target, 
  Award,
  Edit3,
  Save,
  X,
  Upload,
  Download,
  Plus,
  Trash2,
  Sparkles,
  LayoutDashboard,
} from 'lucide-react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { resolveProfileImageUrl } from '@/utils/profileImageUrl';
import api, { profileAPI } from '@/services/api';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ResumeInsightsPanel from '@/components/profile/ResumeInsightsPanel';

interface AspirantProfileData {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  bio?: string;
  dateOfBirth?: string;
  tShirtSize?: string;
  role: string;
  registrationNumber?: string;
  profileImageUrl?: string;
  resumeUrl?: string;
  resumeMetadata?: {
    originalName?: string;
    uploadedAt?: string;
    fileSize?: number;
    s3Key?: string;
  };
  primarySkills?: string[];
  secondarySkills?: string[];
  isEmailVerified: boolean;
  isApproved: boolean;
  profileCompleted: number;
  lookingForJob?: boolean;
  upskilling?: boolean;
  justCommunityMember?: boolean;
  careerGoals?: {
    dreamCompany: string;
    targetRole: string;
    salaryExpectations: string;
    timeline: string;
  };
  education?: {
    degree: string;
    institution: string;
    fieldOfStudy: string;
    graduationYear: number;
    gpa: string;
  };
  skills?: {
    technical: string[];
    soft: string[];
    certifications: string[];
  };
  experience?: Array<{
    _id?: string;
    company: string;
    position: string;
    startDate: string;
    endDate: string;
    isCurrent: boolean;
    description: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

const AspirantProfile: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'profile' ? 'profile' : 'resume-analysis';
  const setActiveTab = (tab: 'profile' | 'resume-analysis') => {
    setSearchParams(tab === 'profile' ? { tab: 'profile' } : {});
  };
  const [profile, setProfile] = useState<AspirantProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<AspirantProfileData>>({});
  const [signedProfileImageUrl, setSignedProfileImageUrl] = useState<string | null>(null);

  // Fetch profile data
  useEffect(() => {
    fetchProfile();
  }, []);

  // Get signed URL for profile image (S3/local only; LinkedIn URLs used directly)
  const getSignedProfileImageUrl = async (imageUrl: string) => resolveProfileImageUrl(imageUrl);

  // Update signed profile image URL when profile changes
  useEffect(() => {
    console.log('Profile image useEffect triggered. Profile:', profile);
    console.log('Profile image URL:', profile?.profileImageUrl);
    
    if (profile?.profileImageUrl) {
      console.log('Calling getSignedProfileImageUrl with:', profile.profileImageUrl);
      getSignedProfileImageUrl(profile.profileImageUrl).then(url => {
        console.log('Signed URL returned:', url);
        setSignedProfileImageUrl(url);
      });
    } else {
      console.log('No profile image URL, setting signed URL to null');
      setSignedProfileImageUrl(null);
    }
  }, [profile?.profileImageUrl]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      // Try using profileAPI first, fallback to direct api call
      let response;
      if (profileAPI && typeof profileAPI.getProfile === 'function') {
        response = await profileAPI.getProfile();
      } else {
        response = await api.get('/profile');
        response = response.data;
      }
      
      if (response.success && response.data) {
        console.log('Profile fetched successfully:', response.data);
        console.log('Profile image URL from backend:', response.data.profileImageUrl);
        
        // Convert Date objects to strings for form inputs
        const profileData = {
          ...response.data,
          experience: response.data.experience?.map((exp: any) => ({
            ...exp,
            startDate: exp.startDate ? new Date(exp.startDate).toISOString().split('T')[0] : '',
            endDate: exp.endDate ? new Date(exp.endDate).toISOString().split('T')[0] : ''
          }))
        };
        
        setProfile(response.data);
        setFormData(profileData);
      } else {
        throw new Error(response.message || 'Failed to fetch profile');
      }
    } catch (err: any) {
      setError('Failed to fetch profile');
      console.error('Profile fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle nested object changes
  const handleNestedChange = (parent: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof AspirantProfileData] as any),
        [field]: value
      }
    }));
  };

  // Handle array field changes
  const handleArrayChange = (parent: string, field: string, value: string[]) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof AspirantProfileData] as any),
        [field]: value
      }
    }));
  };

  // Handle experience changes
  const handleExperienceChange = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      experience: prev.experience?.map((exp, i) => 
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  // Add new experience
  const addExperience = () => {
    const newExperience = {
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      isCurrent: false,
      description: ''
    };
    setFormData(prev => ({
      ...prev,
      experience: [...(prev.experience || []), newExperience]
    }));
  };

  // Remove experience
  const removeExperience = (index: number) => {
    setFormData(prev => ({
      ...prev,
      experience: prev.experience?.filter((_, i) => i !== index)
    }));
  };

  // Save profile
  const saveProfile = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Filter out empty experience entries before sending
      const dataToSend = {
        ...formData,
        experience: formData.experience?.filter(exp => 
          exp.company && exp.company.trim() && 
          exp.position && exp.position.trim() && 
          exp.startDate && exp.startDate.trim()
        ) || []
      };
      
      // Debug logging for interests
      console.log('Saving profile with interests:', {
        lookingForJob: dataToSend.lookingForJob,
        upskilling: dataToSend.upskilling,
        justCommunityMember: dataToSend.justCommunityMember
      });
      
      // Try using profileAPI first, fallback to direct api call
      let response;
      if (profileAPI && typeof profileAPI.updateProfile === 'function') {
        response = await profileAPI.updateProfile(dataToSend);
      } else {
        response = await api.put('/profile', dataToSend);
        response = response.data;
      }
      
      if (response.success && response.data) {
        setProfile(response.data);
        setIsEditing(false);
        setSuccess('Profile updated successfully');
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }
      
    } catch (err: any) {
      console.error('Profile update error:', err);
      console.error('Response data:', err.response?.data);
      
      // Show more specific error message if available
      if (err.response?.data?.details) {
        const validationErrors = err.response.data.details.map((error: any) => error.msg).join(', ');
        setError(`Validation failed: ${validationErrors}`);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to update profile');
      }
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setFormData(profile || {});
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'resume') => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      let response;
      if (type === 'image') {
        response = await profileAPI.uploadProfileImage(file);
      } else {
        response = await profileAPI.uploadResume(file);
      }

      if (response.success) {
        setSuccess(`New ${type} uploaded successfully.`);
        fetchProfile(); // Refresh profile data
      } else {
        throw new Error(response.message || `Failed to upload ${type}`);
      }
    } catch (err: any) {
      if (err.response && err.response.status === 413) {
        setError(err.response.data.message || 'File is too large. Maximum size is 10MB.');
      } else {
        setError(`Failed to upload ${type}. Please try again.`);
      }
      console.error('File upload error:', err);
    } finally {
      setSaving(false);
      // Reset file input to allow re-uploading the same file
      event.target.value = '';
    }
  };

  // Handle resume download
  const handleResumeDownload = async () => {
    if (!profile?.resumeUrl) {
      setError('No resume available for download');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Extract the S3 key from the resume URL
      // Resume URL format: https://bucket.s3.region.amazonaws.com/environment/resumes/filename
      const url = new URL(profile.resumeUrl);
      const fileKey = url.pathname.substring(1); // Remove leading slash
      
      console.log('🔗 Attempting to download resume:', {
        resumeUrl: profile.resumeUrl,
        extractedKey: fileKey
      });

      // Get secure signed URL from backend
      const response = await api.get(`/upload/file/${encodeURIComponent(fileKey)}`);
      
      if (response.data.success && response.data.url) {
        // Use the signed URL for download
        const link = document.createElement('a');
        link.href = response.data.url;
        link.target = '_blank';
        link.download = `${profile.firstName}_${profile.lastName}_Resume.pdf`;
        link.click();
        
        setSuccess('Resume download initiated');
      } else {
        throw new Error('Failed to generate secure download link');
      }
    } catch (err: any) {
      console.error('Resume download error:', err);
      if (err.response?.data?.message) {
        setError(`Download failed: ${err.response.data.message}`);
      } else {
        setError('Failed to download resume. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Debug logging for render
  console.log('RENDER DEBUG - Profile exists:', !!profile);
  console.log('RENDER DEBUG - Profile Image URL:', profile?.profileImageUrl);
  console.log('RENDER DEBUG - Signed URL:', signedProfileImageUrl);

  if (loading) {
    return (
      <LoadingSpinner 
        text="Loading Profile"
        subtitle="Preparing your career journey..."
        size="lg"
      />
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="p-6 bg-red-50 rounded-2xl w-20 h-20 mx-auto flex items-center justify-center shadow-lg">
            <User className="h-10 w-10 text-red-500" />
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-secondary">Profile Not Found</h3>
            <p className="text-gray-600 leading-relaxed">Unable to load your profile information</p>
          </div>
          <Button onClick={fetchProfile} className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-surface shadow-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2 min-w-0">
              <h1 className="text-4xl font-bold text-secondary">Career Profile</h1>
              <p className="text-gray-600 text-lg">
                {activeTab === 'resume-analysis'
                  ? 'AI-powered ATS score, improvements, and career recommendations'
                  : 'Build your professional identity and career goals'}
              </p>
            </div>

            <div className="flex flex-row flex-wrap items-center gap-3 shrink-0">
              <Button
                variant={activeTab === 'resume-analysis' ? 'default' : 'outline'}
                className={
                  activeTab === 'resume-analysis'
                    ? 'bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg'
                    : 'bg-surface border-gray-300 hover:bg-background px-6 py-3 rounded-xl font-semibold'
                }
                onClick={() => setActiveTab('resume-analysis')}
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Resume Analysis
              </Button>

              <Button
                variant="outline"
                className="bg-surface border-gray-300 hover:bg-background px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                onClick={() => navigate('/aspirant/dashboard')}
              >
                <LayoutDashboard className="w-5 h-5 mr-2" />
                Dashboard
              </Button>

              <Button
                variant={activeTab === 'profile' && !isEditing ? 'default' : 'outline'}
                className={
                  activeTab === 'profile' && !isEditing
                    ? 'bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-xl font-semibold shadow-lg'
                    : 'bg-surface border-gray-300 hover:bg-background px-6 py-3 rounded-xl font-semibold'
                }
                onClick={() => {
                  setActiveTab('profile');
                  setIsEditing(false);
                }}
              >
                <User className="w-5 h-5 mr-2" />
                Profile
              </Button>

              {!isEditing ? (
                <Button
                  onClick={() => {
                    setActiveTab('profile');
                    setIsEditing(true);
                  }}
                  className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Edit3 className="w-5 h-5 mr-2" />
                  Edit Profile
                </Button>
              ) : (
                <>
                  <Button
                    onClick={cancelEditing}
                    variant="outline"
                    className="bg-surface border-gray-300 hover:bg-background px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                  >
                    <X className="w-5 h-5 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    onClick={saveProfile}
                    disabled={saving}
                    className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {saving ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Save className="w-5 h-5 mr-2" />
                    )}
                    Save Changes
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-8 bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-xl shadow-sm">
            <div className="flex items-center">
              <div className="w-5 h-5 bg-primary rounded-full mr-3"></div>
              {success}
            </div>
          </div>
        )}
        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-sm">
            <div className="flex items-center">
              <div className="w-5 h-5 bg-red-500 rounded-full mr-3"></div>
              {error}
            </div>
          </div>
        )}

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as 'profile' | 'resume-analysis')}
        >
          <TabsContent value="resume-analysis" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1 space-y-6">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-slate-900">Your Resume</CardTitle>
                    <CardDescription>Upload or download the resume used for analysis</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {profile.resumeUrl ? (
                      <div className="space-y-3">
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-start space-x-2 mb-2">
                            <Download className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm font-medium text-slate-900 break-words">
                              {profile.resumeMetadata?.originalName || 'Resume.pdf'}
                            </span>
                          </div>
                          {profile.resumeMetadata?.uploadedAt && (
                            <div className="text-xs text-slate-600">
                              Uploaded: {new Date(profile.resumeMetadata.uploadedAt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => handleResumeDownload()}
                          disabled={saving}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {saving ? 'Generating...' : 'Download'}
                        </Button>
                        <p className="text-xs text-slate-500">
                          To replace your resume, switch to the Profile tab and click Edit Profile.
                        </p>
                      </div>
                    ) : (
                      <div className="text-center p-4 border-2 border-dashed border-slate-300 rounded-lg">
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600 mb-3">No resume uploaded yet</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setActiveTab('profile');
                            setIsEditing(true);
                          }}
                        >
                          Upload on Profile tab
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              <div className="lg:col-span-2">
                <ResumeInsightsPanel
                  hasResume={Boolean(profile.resumeUrl)}
                  isApproved={Boolean(profile.isApproved)}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="profile" className="mt-0">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Profile Info & Stats */}
          <div className="lg:col-span-1 space-y-8">
            {/* Profile Photo */}
            <Card className="border-border shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-8">
                <div className="text-center space-y-6">
                  <div className="relative w-48 h-48 mx-auto group">
                    <label htmlFor="profile-image-upload" className="cursor-pointer">
                      <div className="w-48 h-48 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-6xl font-bold transition-all duration-300 group-hover:brightness-90">
                        {signedProfileImageUrl ? (
                          <img
                            src={signedProfileImageUrl}
                            alt="Profile"
                            className="w-full h-full rounded-full object-cover"
                            onError={() => {
                              if (profile.profileImageUrl) {
                                setSignedProfileImageUrl(profile.profileImageUrl);
                              }
                            }}
                          />
                        ) : (
                          <User size={80} />
                        )}
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-full flex items-center justify-center transition-all duration-300">
                        <Edit3 className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300" />
                      </div>
                    </label>
                    <input
                      type="file"
                      id="profile-image-upload"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'image')}
                    />
                  </div>
                  <div className="space-y-3">
                    <h2 className="text-3xl font-bold text-secondary">
                      {profile.firstName} {profile.lastName}
                    </h2>
                    <Badge className="bg-primary-light text-blue-800 px-4 py-2 text-sm font-semibold rounded-full">
                      Career Aspirant
                    </Badge>
                    {profile.registrationNumber && (
                      <div className="flex items-center justify-center space-x-2 p-3 bg-background rounded-lg">
                        <span className="text-sm font-semibold text-gray-600">Registration #:</span>
                        <span className="text-lg font-bold text-primary font-mono">{profile.registrationNumber}</span>
                      </div>
                    )}
                    <p className="text-gray-600 text-lg">Building your professional future</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="border-border shadow-lg rounded-2xl overflow-hidden">
              <CardHeader className="bg-background px-8 py-6">
                <CardTitle className="text-xl font-bold text-secondary">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="p-8 space-y-6">
                <div className="flex items-center space-x-4 p-4 bg-background rounded-xl">
                  <div className="p-3 bg-primary-light rounded-full">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Email</p>
                    <p className="text-secondary font-medium">{profile.email}</p>
                  </div>
                </div>
                {profile.phone && (
                  <div className="flex items-center space-x-4 p-4 bg-background rounded-xl">
                    <div className="p-3 bg-primary-light rounded-full">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Phone</p>
                      <p className="text-secondary font-medium">{profile.phone}</p>
                    </div>
                  </div>
                )}
                {profile.address && (
                  <div className="flex items-center space-x-4 p-4 bg-background rounded-xl">
                    <div className="p-3 bg-purple-100 rounded-full">
                      <MapPin className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Address</p>
                      <p className="text-secondary font-medium">{profile.address}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Account Status */}
            <Card className="border-border shadow-lg rounded-2xl overflow-hidden">
              <CardHeader className="bg-background px-8 py-6">
                <CardTitle className="text-xl font-bold text-secondary">Account Status</CardTitle>
              </CardHeader>
              <CardContent className="p-8 space-y-6">
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${profile.isEmailVerified ? 'bg-primary' : 'bg-yellow-500'}`}></div>
                    <span className="text-gray-700 font-medium">Email Verified</span>
                  </div>
                  <Badge className={`px-4 py-2 text-sm font-semibold rounded-full ${
                    profile.isEmailVerified 
                      ? 'bg-primary-light text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {profile.isEmailVerified ? "✓ Verified" : "⚠ Pending"}
                  </Badge>
                </div>
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${profile.isApproved ? 'bg-primary' : 'bg-yellow-500'}`}></div>
                    <span className="text-gray-700 font-medium">Account Status</span>
                  </div>
                  <Badge className={`px-4 py-2 text-sm font-semibold rounded-full ${
                    profile.isApproved 
                      ? 'bg-primary-light text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {profile.isApproved ? "✓ Active" : "⚠ Pending Approval"}
                  </Badge>
                </div>
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Profile Complete</span>
                  <div className="flex items-center space-x-3">
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: `${profile.profileCompleted}%` }}
                      ></div>
                    </div>
                    <span className="text-2xl font-bold text-primary">{profile.profileCompleted}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Resume Upload */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">Resume</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {profile.resumeUrl ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-start space-x-2 mb-2">
                        <Download className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm font-medium text-slate-900 break-words overflow-hidden">
                          {profile.resumeMetadata?.originalName || 'Resume.pdf'}
                        </span>
                      </div>
                      <div className="text-xs text-slate-600">
                        {profile.resumeMetadata?.uploadedAt && (
                          <div>Uploaded: {new Date(profile.resumeMetadata.uploadedAt).toLocaleDateString()}</div>
                        )}
                        {profile.resumeMetadata?.fileSize && (
                          <div>Size: {(profile.resumeMetadata.fileSize / 1024 / 1024).toFixed(2)} MB</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleResumeDownload()}
                        disabled={saving}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {saving ? 'Generating...' : 'Download'}
                      </Button>
                      
                      {isEditing && (
                        <div className="flex-1">
                          <Input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => handleFileUpload(e, 'resume')}
                            className="text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-4 border-2 border-dashed border-slate-300 rounded-lg">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 mb-2">No resume uploaded</p>
                    {isEditing && (
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => handleFileUpload(e, 'resume')}
                        className="text-sm"
                      />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Profile Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">Basic Information</CardTitle>
                <CardDescription>Your personal and contact details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm font-medium text-slate-700">
                      First Name
                    </Label>
                    {isEditing ? (
                      <Input
                        id="firstName"
                        value={formData.firstName || ''}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.firstName}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-medium text-slate-700">
                      Last Name
                    </Label>
                    {isEditing ? (
                      <Input
                        id="lastName"
                        value={formData.lastName || ''}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.lastName}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                      Phone Number
                    </Label>
                    {isEditing ? (
                      <Input
                        id="phone"
                        value={formData.phone || ''}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.phone || 'Not provided'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-sm font-medium text-slate-700">
                      Address
                    </Label>
                    {isEditing ? (
                      <Input
                        id="address"
                        value={formData.address || ''}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.address || 'Not provided'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth" className="text-sm font-medium text-slate-700">
                      Date of Birth (Optional)
                    </Label>
                    {isEditing ? (
                      <Input
                        id="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth || ''}
                        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">
                        {profile.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString() : 'Not provided'}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="tShirtSize" className="text-sm font-medium text-slate-700">
                      T-Shirt Size (Optional)
                    </Label>
                    {isEditing ? (
                      <Select 
                        value={formData.tShirtSize || ''} 
                        onValueChange={(value) => handleInputChange('tShirtSize', value)}
                      >
                        <SelectTrigger className="border-slate-200 focus:border-blue-500 focus:ring-blue-500">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="XS">XS</SelectItem>
                          <SelectItem value="S">S</SelectItem>
                          <SelectItem value="M">M</SelectItem>
                          <SelectItem value="L">L</SelectItem>
                          <SelectItem value="XL">XL</SelectItem>
                          <SelectItem value="XXL">XXL</SelectItem>
                          <SelectItem value="XXXL">XXXL</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.tShirtSize || 'Not provided'}</p>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="bio" className="text-sm font-medium text-slate-700">
                    Bio
                  </Label>
                  {isEditing ? (
                    <Textarea
                      id="bio"
                      value={formData.bio || ''}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      placeholder="Tell us about yourself..."
                      className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      rows={3}
                    />
                  ) : (
                    <p className="text-slate-700">{profile.bio || 'No bio provided'}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Interests & Goals */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-primary" />
                  What Brings You Here?
                </CardTitle>
                <CardDescription>Your interests and goals with our community</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <Label className="text-sm font-medium text-slate-700">Select all that apply:</Label>
                  
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      {isEditing ? (
                        <input
                          type="checkbox"
                          checked={formData.lookingForJob || false}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData({
                              ...formData,
                              lookingForJob: checked,
                              // If job is selected, deselect justCommunityMember
                              justCommunityMember: checked ? false : formData.justCommunityMember
                            });
                          }}
                          className="h-4 w-4 text-primary focus:ring-blue-500 border-gray-300 rounded"
                        />
                      ) : (
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          profile.lookingForJob ? 'bg-primary border-primary' : 'border-gray-300'
                        }`}>
                          {profile.lookingForJob && <div className="w-2 h-2 bg-surface rounded-sm"></div>}
                        </div>
                      )}
                      <label className="text-sm font-medium text-slate-700">
                        Looking for Job Opportunities
                      </label>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {isEditing ? (
                        <input
                          type="checkbox"
                          checked={formData.upskilling || false}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData({
                              ...formData,
                              upskilling: checked,
                              // If upskilling is selected, deselect justCommunityMember
                              justCommunityMember: checked ? false : formData.justCommunityMember
                            });
                          }}
                          className="h-4 w-4 text-primary focus:ring-blue-500 border-gray-300 rounded"
                        />
                      ) : (
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          profile.upskilling ? 'bg-primary border-primary' : 'border-gray-300'
                        }`}>
                          {profile.upskilling && <div className="w-2 h-2 bg-surface rounded-sm"></div>}
                        </div>
                      )}
                      <label className="text-sm font-medium text-slate-700">
                        Upskilling / Learning New Technologies
                      </label>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {isEditing ? (
                        <input
                          type="radio"
                          name="membershipType"
                          checked={formData.justCommunityMember || false}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData({
                              ...formData,
                              justCommunityMember: checked,
                              // If justCommunityMember is selected, deselect the other two
                              lookingForJob: checked ? false : formData.lookingForJob,
                              upskilling: checked ? false : formData.upskilling
                            });
                          }}
                          className="h-4 w-4 text-primary focus:ring-blue-500 border-gray-300"
                        />
                      ) : (
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          profile.justCommunityMember ? 'bg-primary border-primary' : 'border-gray-300'
                        }`}>
                          {profile.justCommunityMember && <div className="w-2 h-2 bg-surface rounded-full"></div>}
                        </div>
                      )}
                      <label className="text-sm font-medium text-slate-700">
                        Just want to be a member of the community
                      </label>
                    </div>
                  </div>
                  
                  {!isEditing && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700">
                        {profile.justCommunityMember 
                          ? "📝 Focused on community networking and knowledge sharing"
                          : `🎯 Interested in: ${[
                              profile.lookingForJob && "Job opportunities",
                              profile.upskilling && "Upskilling"
                            ].filter(Boolean).join(", ") || "Not specified"}`
                        }
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Career Goals */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-primary" />
                  Career Goals
                </CardTitle>
                <CardDescription>Define your career aspirations and targets</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="dreamCompany" className="text-sm font-medium text-slate-700">
                      Dream Companies
                    </Label>
                    {isEditing ? (
                      <Input
                        id="dreamCompany"
                        value={formData.careerGoals?.dreamCompany || ''}
                        onChange={(e) => handleNestedChange('careerGoals', 'dreamCompany', e.target.value)}
                        placeholder="e.g., Google, Microsoft, Amazon"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.careerGoals?.dreamCompany || 'Not specified'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="targetRole" className="text-sm font-medium text-slate-700">
                      Target Role
                    </Label>
                    {isEditing ? (
                      <Input
                        id="targetRole"
                        value={formData.careerGoals?.targetRole || ''}
                        onChange={(e) => handleNestedChange('careerGoals', 'targetRole', e.target.value)}
                        placeholder="e.g., Full Stack Developer"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.careerGoals?.targetRole || 'Not specified'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="salaryExpectations" className="text-sm font-medium text-slate-700">
                      Salary Expectations
                    </Label>
                    {isEditing ? (
                      <Input
                        id="salaryExpectations"
                        value={formData.careerGoals?.salaryExpectations || ''}
                        onChange={(e) => handleNestedChange('careerGoals', 'salaryExpectations', e.target.value)}
                        placeholder="e.g., 8-12 LPA"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.careerGoals?.salaryExpectations || 'Not specified'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="timeline" className="text-sm font-medium text-slate-700">
                      Timeline
                    </Label>
                    {isEditing ? (
                      <Input
                        id="timeline"
                        value={formData.careerGoals?.timeline || ''}
                        onChange={(e) => handleNestedChange('careerGoals', 'timeline', e.target.value)}
                        placeholder="e.g., 6-12 months"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.careerGoals?.timeline || 'Not specified'}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Education */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <GraduationCap className="w-5 h-5 mr-2 text-primary" />
                  Education
                </CardTitle>
                <CardDescription>Your academic background and qualifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="degree" className="text-sm font-medium text-slate-700">
                      Degree
                    </Label>
                    {isEditing ? (
                      <Input
                        id="degree"
                        value={formData.education?.degree || ''}
                        onChange={(e) => handleNestedChange('education', 'degree', e.target.value)}
                        placeholder="e.g., Bachelor of Technology"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.education?.degree || 'Not specified'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="institution" className="text-sm font-medium text-slate-700">
                      Institution
                    </Label>
                    {isEditing ? (
                      <Input
                        id="institution"
                        value={formData.education?.institution || ''}
                        onChange={(e) => handleNestedChange('education', 'institution', e.target.value)}
                        placeholder="e.g., Indian Institute of Technology"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.education?.institution || 'Not specified'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="fieldOfStudy" className="text-sm font-medium text-slate-700">
                      Field of Study
                    </Label>
                    {isEditing ? (
                      <Input
                        id="fieldOfStudy"
                        value={formData.education?.fieldOfStudy || ''}
                        onChange={(e) => handleNestedChange('education', 'fieldOfStudy', e.target.value)}
                        placeholder="e.g., Computer Science"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.education?.fieldOfStudy || 'Not specified'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="graduationYear" className="text-sm font-medium text-slate-700">
                      Graduation Year
                    </Label>
                    {isEditing ? (
                      <Input
                        id="graduationYear"
                        type="number"
                        value={formData.education?.graduationYear || ''}
                        onChange={(e) => handleNestedChange('education', 'graduationYear', parseInt(e.target.value))}
                        placeholder="e.g., 2024"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.education?.graduationYear || 'Not specified'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="gpa" className="text-sm font-medium text-slate-700">
                      GPA/CGPA
                    </Label>
                    {isEditing ? (
                      <Input
                        id="gpa"
                        value={formData.education?.gpa || ''}
                        onChange={(e) => handleNestedChange('education', 'gpa', e.target.value)}
                        placeholder="e.g., 8.5/10"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.education?.gpa || 'Not specified'}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Skills */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <Award className="w-5 h-5 mr-2 text-primary" />
                  Skills & Certifications
                </CardTitle>
                <CardDescription>Your technical and soft skills</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Primary Skills</Label>
                    {isEditing ? (
                      <Input
                        value={typeof formData.primarySkills === 'string' ? formData.primarySkills : formData.primarySkills?.join(', ') || ''}
                        onChange={(e) => handleInputChange('primarySkills', e.target.value)}
                        onBlur={(e) => {
                          const skills = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                          handleInputChange('primarySkills', skills);
                        }}
                        placeholder="e.g., Java, React, Financial Reporting, Accounting"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {profile.primarySkills?.map((skill, index) => (
                          <Badge key={index} variant="secondary">{skill}</Badge>
                        )) || <p className="text-slate-500">No primary skills listed</p>}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Secondary Skills</Label>
                    {isEditing ? (
                      <Input
                        value={typeof formData.secondarySkills === 'string' ? formData.secondarySkills : formData.secondarySkills?.join(', ') || ''}
                        onChange={(e) => handleInputChange('secondarySkills', e.target.value)}
                        onBlur={(e) => {
                          const skills = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                          handleInputChange('secondarySkills', skills);
                        }}
                        placeholder="e.g., Python, Node.js, Team Leadership, Project Management"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {profile.secondarySkills?.map((skill, index) => (
                          <Badge key={index} variant="outline">{skill}</Badge>
                        )) || <p className="text-slate-500">No secondary skills listed</p>}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Certifications</Label>
                    {isEditing ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            id="certificationInput"
                            placeholder="e.g., AWS Certified Developer, Google Cloud Professional"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const input = e.target as HTMLInputElement;
                                const value = input.value.trim();
                                if (value && !formData.skills?.certifications?.includes(value)) {
                                  const currentCerts = formData.skills?.certifications || [];
                                  handleArrayChange('skills', 'certifications', [...currentCerts, value]);
                                  input.value = '';
                                }
                              }
                            }}
                            className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              const input = document.getElementById('certificationInput') as HTMLInputElement;
                              const value = input.value.trim();
                              if (value && !formData.skills?.certifications?.includes(value)) {
                                const currentCerts = formData.skills?.certifications || [];
                                handleArrayChange('skills', 'certifications', [...currentCerts, value]);
                                input.value = '';
                              }
                            }}
                            className="border-slate-200 hover:border-blue-500"
                          >
                            Add Certification
                          </Button>
                        </div>
                        {formData.skills?.certifications && formData.skills.certifications.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {formData.skills.certifications.map((cert, index) => (
                              <Badge
                                key={index}
                                variant="secondary"
                                className="cursor-pointer hover:bg-red-100"
                                onClick={() => {
                                  const updatedCerts = formData.skills?.certifications?.filter((_, i) => i !== index) || [];
                                  handleArrayChange('skills', 'certifications', updatedCerts);
                                }}
                              >
                                {cert} ×
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {profile.skills?.certifications?.map((cert, index) => (
                          <Badge key={index} variant="default">{cert}</Badge>
                        )) || <p className="text-slate-500">No certifications listed</p>}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Experience */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <Briefcase className="w-5 h-5 mr-2 text-primary" />
                  Work Experience
                </CardTitle>
                <CardDescription>Your professional experience and internships</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {formData.experience?.map((exp, index) => (
                  <div key={exp._id} className="p-4 border border-slate-200 rounded-lg space-y-4">
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium text-slate-900">Experience #{index + 1}</h4>
                      {isEditing && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeExperience(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Company</Label>
                        {isEditing ? (
                          <Input
                            value={exp.company}
                            onChange={(e) => handleExperienceChange(index, 'company', e.target.value)}
                            placeholder="Company name"
                            className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                          />
                        ) : (
                          <p className="text-slate-900">{exp.company || 'Not specified'}</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Position</Label>
                        {isEditing ? (
                          <Input
                            value={exp.position}
                            onChange={(e) => handleExperienceChange(index, 'position', e.target.value)}
                            placeholder="Job title"
                            className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                          />
                        ) : (
                          <p className="text-slate-900">{exp.position || 'Not specified'}</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Start Date</Label>
                        {isEditing ? (
                          <Input
                            type="date"
                            value={exp.startDate}
                            onChange={(e) => handleExperienceChange(index, 'startDate', e.target.value)}
                            className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                          />
                        ) : (
                          <p className="text-slate-900">
                            {exp.startDate ? new Date(exp.startDate).toLocaleDateString() : 'Not specified'}
                          </p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">End Date</Label>
                        {isEditing ? (
                          <Input
                            type="date"
                            value={exp.endDate}
                            onChange={(e) => handleExperienceChange(index, 'endDate', e.target.value)}
                            disabled={exp.isCurrent}
                            className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                          />
                        ) : (
                          <p className="text-slate-900">
                            {exp.isCurrent ? 'Present' : (exp.endDate ? new Date(exp.endDate).toLocaleDateString() : 'Not specified')}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">Description</Label>
                      {isEditing ? (
                        <Textarea
                          value={exp.description}
                          onChange={(e) => handleExperienceChange(index, 'description', e.target.value)}
                          placeholder="Describe your role and achievements..."
                          className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                          rows={3}
                        />
                      ) : (
                        <p className="text-slate-700">{exp.description || 'No description provided'}</p>
                      )}
                    </div>
                  </div>
                ))}
                
                {isEditing && (
                  <div className="space-y-3">
                    <Button
                      onClick={addExperience}
                      variant="outline"
                      className="w-full border-dashed border-slate-300"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Experience
                    </Button>
                    <p className="text-sm text-slate-500 text-center">
                      💡 Tip: Company, position, and start date are required. Description and end date are optional.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AspirantProfile; 